/* FileDropField — small shared component used in lesson modals (admin +
 * instructor). Lets the user drag a PDF / video / slide deck into a dotted
 * box (or click to browse), uploads it via /api/academy/uploads, and emits
 * the resulting {label, url, kind} resource entry to the parent.
 *
 * Auth is cookie-based; no extra config needed at the call site. */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, AlertCircle } from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });
const MAX_MB = 25;

export default function FileDropField({ onUploaded, compact = false }) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File too large — max ${MAX_MB} MB`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/academy/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div data-testid="file-drop-field">
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex items-center justify-center gap-2 cursor-pointer rounded-lg border-2 border-dashed transition-colors
          ${dragOver ? 'border-[#c9a227] bg-[#fffbf0]' : 'border-[#E5E5E7] bg-[#FAFAFA] hover:border-[#c9a227]'}
          ${compact ? 'px-3 py-2 text-[12px]' : 'px-4 py-4 text-[12.5px]'}`}
      >
        <input ref={inputRef} type="file" className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.mp4,.webm,.mov,.m4v,.ppt,.pptx,.doc,.docx,.csv,.txt,.md"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="file-drop-input" />
        {busy ? (
          <><Loader2 className="w-4 h-4 animate-spin text-[#c9a227]" /> <span className="font-semibold">Uploading…</span></>
        ) : (
          <><Upload className="w-4 h-4 text-[#c9a227]" />
            <span className="font-semibold text-[#0e0e10]">Drop a file or click</span>
            <span className="text-[#6E6E73]">PDF, video, slides ({MAX_MB} MB max)</span></>
        )}
      </label>
      {error && (
        <div className="mt-1.5 inline-flex items-start gap-1.5 text-[11.5px] text-red-700" data-testid="file-drop-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> {error}
        </div>
      )}
    </div>
  );
}
